/**
 * CSVファイル処理サービス
 * ブラウザ環境でのCSVパース、データ型推論、データセット変換
 */

import Papa from 'papaparse';
import { DataSet, PrimaryDataSet, DatasetId, ColumnId, createColumnId, createDatasetId, createColumnName } from '../../types';
import { DataValue, DataSchema } from '../../types/core.types';
import { ROW_ID_COLUMN_ID, ROW_ID_COLUMN_NAME } from '../../types/system-columns';
import { inferColumnType, convertValues } from './dataTypeInferrer';
import { inferScale } from './scaleInferrer';
import { validateMdsUrl } from '../utils/urlValidator';
import logger from '../utils/logger';

/**
 * 列IDを生成
 */
function generateColumnId(baseName: string): ColumnId {
  const timestamp = Date.now().toString(16);
  const random = Math.random().toString(36).substring(2, 8);
  const cleanBase = baseName.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 20);
  return createColumnId(`col_${cleanBase}_${timestamp}_${random}`);
}

/**
 * データセットIDを生成
 */
function generateDatasetId(): DatasetId {
  const timestamp = Date.now().toString(16);
  const random = Math.random().toString(36).substring(2, 8);
  return createDatasetId(`dataset_${timestamp}_${random}`);
}

/**
 * 重複列名を解決する
 * 重複があれば連番を付ける（Name, Name_2, Name_3...）
 * @returns 解決された列名の配列と、重複が見つかった列名のリスト
 */
function resolveColumnNameDuplicates(headers: string[]): {
  resolvedHeaders: string[];
  duplicates: string[];
} {
  const resolvedHeaders: string[] = [];
  const counts = new Map<string, number>();
  const duplicates = new Set<string>();

  headers.forEach(name => {
    const count = counts.get(name) || 0;
    counts.set(name, count + 1);

    if (count === 0) {
      // 初出の列名
      resolvedHeaders.push(name);
    } else {
      // 重複している列名
      duplicates.add(name);
      resolvedHeaders.push(`${name}_${count + 1}`);
    }
  });

  return {
    resolvedHeaders,
    duplicates: Array.from(duplicates)
  };
}

/**
 * CSVパース結果の型
 */
interface ParsedCSVData {
  columnIds: Record<string, ColumnId>;
  columnData: Record<ColumnId, DataValue[]>;
  columnNames: string[];
  rowCount: number;
}

/**
 * CSVコンテンツをパースして列データに変換する共通関数
 */
function parseCSVContent(
  content: string,
  options?: {
    maxRows?: number;
    hasHeader?: boolean;
    existingSchema?: DataSchema;
  }
): { parsed: ParsedCSVData; warnings: string[] } {
  const warnings: string[] = [];

  // ヘッダーの有無を自動検出（未指定の場合）
  const hasHeader = options?.hasHeader ?? detectHeader(content);

  // PapaParseでCSVをパース
  const parseResult = Papa.parse(content, {
    header: false,
    skipEmptyLines: true,
    preview: options?.maxRows ? (hasHeader ? options.maxRows + 1 : options.maxRows) : 0,
    dynamicTyping: false,
  });

  if (parseResult.errors.length > 0) {
    parseResult.errors.forEach(error => {
      warnings.push(`CSV Parse Warning: ${error.message} at row ${error.row}`);
    });
  }

  const rows = parseResult.data as string[][];
  if (rows.length === 0) {
    throw new Error('No data found in CSV');
  }

  let columnIds: Record<string, ColumnId> = {};
  let columnData: Record<ColumnId, DataValue[]> = {};
  let columnNames: string[] = [];

  if (hasHeader) {
    const originalHeaders = rows[0];
    const dataRows = rows.slice(1);
    const { resolvedHeaders, duplicates } = resolveColumnNameDuplicates(originalHeaders);

    if (duplicates.length > 0) {
      duplicates.forEach(dupName => {
        warnings.push(`Duplicate column name "${dupName}" found. Renamed with sequential numbers (e.g., ${dupName}_2, ${dupName}_3).`);
      });
    }

    columnNames = resolvedHeaders;
    resolvedHeaders.forEach(name => {
      const existingColumn = options?.existingSchema?.columns.find(col => col.name === name);
      columnIds[name] = existingColumn ? existingColumn.id : generateColumnId(name);
      columnData[columnIds[name]] = [];
    });

    dataRows.forEach(row => {
      resolvedHeaders.forEach((name, index) => {
        const value = row[index];
        columnData[columnIds[name]].push(value === undefined || value === '' ? null : value);
      });
    });
  } else {
    const numColumns = rows[0].length;

    for (let i = 0; i < numColumns; i++) {
      const name = `Column${i + 1}`;
      columnNames.push(name);
      const existingColumn = options?.existingSchema?.columns.find(col => col.name === name);
      columnIds[name] = existingColumn ? existingColumn.id : generateColumnId(name);
      columnData[columnIds[name]] = [];
    }

    rows.forEach(row => {
      columnNames.forEach((name, i) => {
        const value = row[i];
        columnData[columnIds[name]].push(value === undefined || value === '' ? null : value);
      });
    });
  }

  // ROW_ID列を追加
  const rowCount = Object.values(columnData)[0]?.length || 0;
  columnData[ROW_ID_COLUMN_ID] = Array.from({ length: rowCount }, (_, i) => i);

  return {
    parsed: { columnIds, columnData, columnNames, rowCount },
    warnings
  };
}

/**
 * 列データからスキーマを構築する共通関数
 */
function buildSchema(
  columnIds: Record<string, ColumnId>,
  columnData: Record<ColumnId, DataValue[]>,
  columnNames: string[]
): DataSchema {
  const columns = [
    {
      id: ROW_ID_COLUMN_ID,
      name: ROW_ID_COLUMN_NAME,
      type: 'int64' as const,
      nullable: false,
    },
    ...columnNames.map(name => {
      const columnId = columnIds[name];
      const values = columnData[columnId];
      const dataType = inferColumnType(values);
      const convertedValues = convertValues(values, dataType);
      columnData[columnId] = convertedValues as DataValue[];
      const scale = inferScale(dataType, convertedValues as DataValue[]);

      return {
        id: columnId,
        name: createColumnName(name),
        originalName: name,
        type: dataType,
        nullable: values.some(v => v === null || v === undefined || v === ''),
        scale,
      };
    })
  ];

  return { columns };
}

/**
 * CSVファイルをパースしてDataSet形式に変換
 */
export async function parseCSVToDataset(
  file: File,
  datasetId?: DatasetId,
  options?: {
    maxRows?: number;
    hasHeader?: boolean;
    existingSchema?: DataSchema;
  }
): Promise<{ dataset: DataSet; warnings: string[] }> {
  const id = datasetId || generateDatasetId();

  // ファイルを文字列として読み込む
  const content = await file.text();

  // 共通関数でパース
  const { parsed, warnings } = parseCSVContent(content, options);
  const { columnIds, columnData, columnNames, rowCount } = parsed;

  // スキーマを構築
  const schema = buildSchema(columnIds, columnData, columnNames);

  // DataSet形式に変換
  const dataset: PrimaryDataSet = {
    id,
    name: file.name.replace(/\.(csv|tsv)$/i, ''),
    type: 'primary',
    schema,
    rowCount,
    data: columnData,
    metadata: {
      originalFileName: file.name,
      importedAt: new Date().toISOString(),
      fileSize: file.size,
    },
  };

  return { dataset, warnings };
}

/**
 * ヘッダーの有無を自動検出
 */
function detectHeader(content: string): boolean {
  // 最初の2行を取得
  const lines = content.split('\n').slice(0, 2).filter(line => line.trim());
  if (lines.length < 2) {
    return true; // データが1行以下の場合はヘッダーありと仮定
  }

  // Papa Parseで最初の2行をパース
  const firstRow = Papa.parse(lines[0], { header: false }).data[0] as string[];
  const secondRow = Papa.parse(lines[1], { header: false }).data[0] as string[];

  if (!firstRow || !secondRow) {
    return true;
  }

  // 各列のデータ型を分析
  const firstRowTypes = firstRow.map(analyzeValueType);
  const secondRowTypes = secondRow.map(analyzeValueType);

  // 1行目が全て文字列で、2行目に数値が含まれる場合はヘッダーありと判定
  const firstRowAllText = firstRowTypes.every(type => type === 'string');
  const secondRowHasNumbers = secondRowTypes.some(type => type === 'numeric' || type === 'numeric_string');

  if (firstRowAllText && secondRowHasNumbers) {
    return true;
  }

  // 1行目に数値が含まれる場合はヘッダーなしと判定
  const firstRowHasNumbers = firstRowTypes.some(type => type === 'numeric' || type === 'numeric_string');
  if (firstRowHasNumbers) {
    return false;
  }

  // デフォルトはヘッダーありと仮定
  return true;
}

/**
 * 値のデータ型を分析
 */
function analyzeValueType(value: string): 'numeric' | 'numeric_string' | 'string' {
  if (!value || value.trim() === '') {
    return 'string';
  }

  const trimmed = value.trim();

  // 数値として解釈可能か
  const num = Number(trimmed.replace(/,/g, ''));
  if (!isNaN(num)) {
    return 'numeric_string';
  }

  return 'string';
}


/**
 * CSVファイルにエクスポート
 */
export function exportToCSV(
  dataset: DataSet,
  options?: {
    delimiter?: string;
    includeRowId?: boolean;
  }
): string {
  const delimiter = options?.delimiter || ',';
  const includeRowId = options?.includeRowId ?? false;

  // エクスポートする列を決定
  const columnsToExport = includeRowId
    ? dataset.schema.columns
    : dataset.schema.columns.filter(col => col.id !== ROW_ID_COLUMN_ID);

  // ヘッダー行
  const headers = columnsToExport.map(col => col.name);

  // データ行
  const rows: Array<Record<string, unknown>> = [];
  const rowCount = dataset.rowCount ?? 0;

  for (let i = 0; i < rowCount; i++) {
    const row: Record<string, unknown> = {};
    columnsToExport.forEach(col => {
      const data = dataset.data as Record<string, DataValue[]> | undefined;
      row[col.name] = data?.[col.id]?.[i] ?? null;
    });
    rows.push(row);
  }

  // PapaParseでCSV文字列に変換
  return Papa.unparse({
    fields: headers,
    data: rows
  }, {
    delimiter,
  });
}

/**
 * 進捗コールバックの型
 */
export interface FetchProgress {
  phase: 'connecting' | 'downloading' | 'parsing';
  bytesLoaded?: number;
  bytesTotal?: number;
  percent?: number;
}

/**
 * URLからCSVをfetchしてDataSet形式に変換
 */
export async function fetchCSVFromUrl(
  url: string,
  datasetId?: DatasetId,
  options?: {
    maxRows?: number;
    hasHeader?: boolean;
    existingSchema?: DataSchema;
    timeout?: number;
    signal?: AbortSignal;
    onProgress?: (progress: FetchProgress) => void;
  }
): Promise<{ dataset: PrimaryDataSet; warnings: string[] }> {
  const allWarnings: string[] = [];
  const id = datasetId || generateDatasetId();
  const timeout = options?.timeout ?? 30000; // デフォルト30秒

  logger.info('[csvProcessor] Fetching CSV from URL:', url);

  // URL検証（MDSと同じルールを適用）
  const validation = validateMdsUrl(url);
  if (!validation.allowed) {
    throw new Error(validation.reason || 'URL is not allowed');
  }
  if (validation.needsWarning) {
    allWarnings.push(`Security warning: ${validation.reason}`);
  }

  // AbortController for timeout（外部からのsignalがあればそれも使用）
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // 外部からのAbortSignalがある場合、それもリッスン
  const handleAbort = () => controller.abort();
  if (options?.signal) {
    options.signal.addEventListener('abort', handleAbort);
  }

  const onProgress = options?.onProgress;

  try {
    onProgress?.({ phase: 'connecting' });

    const response = await fetch(url, {
      signal: controller.signal,
      redirect: 'error', // リダイレクト攻撃を防ぐ
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Content-Type検証
    const contentType = response.headers.get('Content-Type')?.toLowerCase() || '';
    const allowedContentTypes = [
      'text/csv',
      'text/plain',
      'text/tab-separated-values',
      'application/csv',
      'application/x-csv',
    ];
    const disallowedContentTypes = [
      'text/html',
      'application/json',
      'application/xml',
      'text/xml',
      'image/',
      'video/',
      'audio/',
      'application/pdf',
      'application/zip',
      'application/octet-stream',
    ];

    // Content-Typeのメディアタイプ部分を抽出（charset等のパラメータを除去）
    const mediaType = contentType.split(';')[0].trim();

    // 明らかにCSVでないContent-Typeはエラー
    const isDisallowed = disallowedContentTypes.some(t => mediaType.startsWith(t));
    if (isDisallowed) {
      throw new Error(`Invalid content type: ${mediaType}. Expected CSV data.`);
    }

    // 許可リストにない場合は警告（パース失敗時にエラーになる）
    const isAllowed = allowedContentTypes.some(t => mediaType === t);
    if (mediaType && !isAllowed) {
      allWarnings.push(`Unexpected content type: ${mediaType}. Attempting to parse as CSV.`);
      logger.warn('[csvProcessor] Unexpected Content-Type:', mediaType);
    }

    // Content-Lengthチェック（10MB制限）
    const contentLength = response.headers.get('Content-Length');
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const totalBytes = contentLength ? parseInt(contentLength, 10) : undefined;

    if (totalBytes && totalBytes > MAX_SIZE) {
      throw new Error(`File size exceeds 10MB limit (${Math.round(totalBytes / 1024 / 1024)}MB)`);
    }

    // ReadableStreamを使って進捗を報告
    let content: string;
    if (response.body && onProgress) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const chunks: string[] = [];
      let loadedBytes = 0;

      onProgress({ phase: 'downloading', bytesLoaded: 0, bytesTotal: totalBytes });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        loadedBytes += value.length;
        chunks.push(decoder.decode(value, { stream: true }));

        const percent = totalBytes ? Math.round((loadedBytes / totalBytes) * 100) : undefined;
        onProgress({ phase: 'downloading', bytesLoaded: loadedBytes, bytesTotal: totalBytes, percent });
      }

      content = chunks.join('') + decoder.decode(); // flush remaining
    } else {
      content = await response.text();
    }

    // ダウンロード後のサイズチェック
    if (content.length > MAX_SIZE) {
      throw new Error(`File size exceeds 10MB limit`);
    }

    // パース開始
    onProgress?.({ phase: 'parsing' });

    // 共通関数でパース
    const { parsed, warnings } = parseCSVContent(content, options);
    allWarnings.push(...warnings);
    const { columnIds, columnData, columnNames, rowCount } = parsed;

    // スキーマを構築
    const schema = buildSchema(columnIds, columnData, columnNames);

    // URLからファイル名を抽出
    const urlPath = new URL(url).pathname;
    let fileName = urlPath.split('/').pop() || 'data.csv';
    // 拡張子がない場合はデフォルト名を使用
    if (!fileName.includes('.')) {
      fileName = 'data.csv';
    }

    const dataset: PrimaryDataSet = {
      id,
      name: fileName.replace(/\.(csv|tsv)$/i, ''),
      type: 'primary',
      schema,
      rowCount,
      data: columnData,
      metadata: {
        originalFileName: fileName,
        importedAt: new Date().toISOString(),
        fileSize: content.length,
        sourceUrl: url,
        sourceType: 'url',
        lastFetchedAt: new Date().toISOString(),
      },
    };

    logger.info('[csvProcessor] Successfully fetched CSV from URL:', {
      url,
      rowCount,
      columnCount: schema.columns.length,
    });

    // クリーンアップ
    if (options?.signal) {
      options.signal.removeEventListener('abort', handleAbort);
    }

    return { dataset, warnings: allWarnings };

  } catch (error) {
    clearTimeout(timeoutId);

    // クリーンアップ
    if (options?.signal) {
      options.signal.removeEventListener('abort', handleAbort);
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeout / 1000} seconds`);
      }
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Failed to fetch. Check if the URL is accessible and CORS is configured.');
      }
    }
    throw error;
  }
}